/**
 * Backups: every organization's backups, on the host. The list says which organizations are backed up
 * and which are not; choosing one opens its settings, archives and run log beneath it.
 *
 * Needs Manage organizations, and shows only the organizations the operator can reach. Changing,
 * running, downloading and restoring need an administrator assignment in that organization; a
 * technician assignment sees the same page read-only.
 */
import { insets, type Container } from 'webwin';
import * as hostAccess from '../api/hostAccess.js';
import * as backups from '../api/hostBackups.js';
import { describeError } from '../api/http.js';
import { HostPermission, type HostTenantBackupDto } from '../contracts.gen.js';
import { backupStateLine } from '../hostDashboard.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  FONT,
  GAP,
  h1,
  inlineRow,
  muted,
  relayout,
  scrollPage,
  stackOf,
  text,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';
import { backupSection } from './sections/backups.js';

export function hostBackupsPage(): CrmPage {
  return {
    mount(host_: Container, ctx: PageContext) {
      const { view, content } = scrollPage();
      host_.controls.add(view);

      const status = new StatusLine();
      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        h1('Backups'),
        muted(
          "Every organization's backups, each written to a folder of its own and restorable only into the organization it came from. Choose an organization to set where and when its backups go, take one now, or restore one."
        )
      );
      content.controls.add(header);

      if (hostAccess.isLoaded() && !hostAccess.has(HostPermission.ManageOrganizations)) {
        const notice = card();
        notice.controls.add(text('Backups need the Manage organizations permission. Another host operator with Manage operators can grant it.'));
        content.controls.add(notice);
        relayout(content);
        return;
      }

      header.controls.add(stackOf(buttonRow(), button('Refresh', () => void loadList())));
      header.controls.add(status.label);

      let rows: HostTenantBackupDto[] = [];
      let selected = Number(ctx.query.get('org')) || 0;

      const empty = muted('You are not assigned to any organization.');
      const list = new CardList<HostTenantBackupDto>({
        key: t => String(t.tenantId),
        version: t => JSON.stringify([t, t.tenantId === selected]),
        build: t => orgRow(t),
        spacing: GAP.xs
      });
      const detail = vstack({ spacing: GAP.lg });
      content.controls.addRange([empty, list.panel, detail]);

      function orgRow(t: HostTenantBackupDto): Card {
        const c = t.config;
        const line = backupStateLine({
          running: t.running,
          scheduled: c.runsOnSchedule,
          lastRunUtc: c.lastRunUtc,
          lastRunSucceeded: c.lastRunUtc ? c.lastRunSucceeded : null,
          lastSuccessUtc: c.lastSuccessUtc
        });
        const heading = inlineRow();
        heading.controls.add(value(t.tenantName, { font: FONT.bold }));
        heading.controls.add(chip(line.tone === 'ok' ? 'backed up' : line.tone === 'danger' ? 'failed' : line.tone === 'neutral' ? 'not running' : 'check', line.tone));
        if (t.restorePending) heading.controls.add(chip('restore staged', 'warn'));
        if (!t.canManage) heading.controls.add(chip('read only', 'neutral'));
        const shell = card({ tint: t.tenantId === selected ? 'info' : 'card', padding: insets(10, 8), cursor: 'pointer' });
        shell.controls.add(
          stackOf(
            vstack({ spacing: GAP.xs }),
            heading,
            muted(line.text),
            muted(c.isConfigured ? c.destinationPath : 'No destination folder yet.')
          )
        );
        shell.onActivate(() => select(t.tenantId));
        return shell;
      }

      function select(tenantId: number): void {
        const t = rows.find(r => r.tenantId === tenantId);
        if (!t) return;
        selected = tenantId;
        const url = new URL(window.location.href);
        url.searchParams.set('org', String(tenantId));
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        list.set(rows);

        detail.controls.clear();
        const section = backupSection(ctx, backups.forOrganization(tenantId), {
          organization: t.tenantName,
          canEdit: t.canManage,
          onChanged: () => void loadList()
        });
        detail.controls.add(section.card);
        relayout(detail);
        void section.load().then(() => {
          if (!ctx.cancelled()) relayout(detail);
        });
      }

      async function loadList(): Promise<void> {
        try {
          rows = await backups.list(ctx.dispose.signal());
          if (ctx.cancelled()) return;
          empty.visible = rows.length === 0;
          if (!rows.some(r => r.tenantId === selected)) selected = rows.length === 1 ? rows[0]!.tenantId : 0;
          list.set(rows);
          if (selected && detail.controls.length === 0) select(selected);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the organizations: ' + describeError(error));
        }
      }

      empty.visible = false;
      void loadList();
    }
  };
}
